import { HttpErrorResponse } from '@angular/common/http'
import type { Anrede } from '../provider'

/**
 * Warum eine Abfrage fehlgeschlagen ist — in der Aufloesung, die fuer die Nutzer:in einen
 * Unterschied macht. Bewusst nicht der HTTP-Status: „503" und „Verbindung abgerissen" fuehlen
 * sich gleich an (SaniUp antwortet nicht) und brauchen denselben Satz, waehrend 401 und 403
 * gleich aussehen, aber Verschiedenes bedeuten (neu anmelden vs. gar nicht erst duerfen).
 */
export type HttpFailureKind =
  /** Das Geraet selbst ist ohne Netz. */
  | 'offline'
  /** Netz da, SaniUp antwortet nicht (Verbindung abgewiesen, DNS, CORS-Abbruch). */
  | 'unreachable'
  /** SaniUp antwortet, ist aber nicht bereit — typischerweise ein Neustart nach einem Deploy. */
  | 'unavailable'
  /** SaniUp antwortet mit einem Fehler. Ein zweiter Versuch aendert daran nichts. */
  | 'server'
  /** Die Sitzung traegt nicht mehr. */
  | 'unauthorized'
  /** Angemeldet, aber fuer diese Daten nicht freigeschaltet. */
  | 'forbidden'
  | 'notFound'
  | 'unknown'

export interface HttpFailureCopy {
  icon: string
  title: string
  message: string
  /** Beschriftung der Wiederholen-Aktion — `null`, wo ein zweiter Versuch nichts aendern kann. */
  retryLabel: string | null
}

/**
 * Fehlerarten, bei denen ein spaeterer Versuch die Sache von allein loest. Nur an diesen haengt
 * die Selbstheilung (`ConnectionService`) — ein 500er wuerde sonst im Sekundentakt nachgeschossen,
 * ohne dass sich je etwas aendert.
 */
const RECOVERABLE: readonly HttpFailureKind[] = ['offline', 'unreachable', 'unavailable']

export function isRecoverableFailure(kind: HttpFailureKind): boolean {
  return RECOVERABLE.includes(kind)
}

/**
 * Belegt dieser Fehler, dass SaniUp geantwortet hat?
 *
 * Eine Fehlerantwort ist immer noch eine Antwort: 401, 404 und 500 beweisen die Erreichbarkeit
 * genauso gut wie ein 200er. Ohne diese Unterscheidung blieb der Verbindungsstreifen stehen,
 * sobald die wiederholte Abfrage zwar ankam, aber fachlich fehlschlug — der `ConnectionService`
 * sah nur „wieder ein Fehler" und liess die Selbstheilung weiterlaufen.
 *
 * Zwei Faelle zaehlen bewusst NICHT als Beleg: Status 0 (die Antwort ist nie angekommen) und
 * 502/503/504 — dort hat zwar die Azure-Plattform geantwortet, der Dienst startet aber noch, und
 * genau dafuer soll die Leiter weiterlaufen.
 */
export function provesReachability(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false
  }
  return !isRecoverableFailure(classifyHttpFailure(error))
}

/**
 * Hat das Backend diesen Fehler bereits selbst erklaert?
 *
 * Fachliche Ablehnungen kommen als 409 mit dem Header `X-Error: true` und einem Enum-Tag im Body;
 * der `errorMsgInterceptor` macht daraus einen Dialog mit dem passenden Text. Ein `catch` im
 * Aufrufer, das daraufhin noch ein rotes „… konnte nicht … werden" in die Snackbar legt, sagt
 * dasselbe ein zweites Mal — nur schlechter, weil es den Grund nicht kennt.
 *
 * Genau so sah bis 08/2026 jede abgelehnte Loeschung aus: Dialog mit dem Grund UND Snackbar ohne
 * ihn, gleichzeitig. Wer einen Aufruf abfaengt, der fachlich abgelehnt werden kann, prueft das
 * hier und schweigt in dem Fall:
 *
 * ```ts
 * catch (error) {
 *   if (!isHandledByErrorMsgDialog(error)) {
 *     this.snackbarService.showError('… konnte nicht … werden.')
 *   }
 * }
 * ```
 */
export function isHandledByErrorMsgDialog(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.headers.get('X-Error') === 'true'
}

function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine
}

/**
 * Ordnet einen Fehler aus einer HTTP-Abfrage einer Fehlerart zu.
 *
 * `online` ist absichtlich ein Parameter und kein Zugriff auf `navigator` mitten in der Funktion:
 * so bleibt sie rein und damit pruefbar.
 */
export function classifyHttpFailure(error: unknown, online: boolean = isOnline()): HttpFailureKind {
  if (!(error instanceof HttpErrorResponse)) {
    // Kein Netzwerkfehler, sondern z.B. ein Fehler im Mapping der Antwort.
    return 'unknown'
  }

  // Status 0 heisst: die Antwort ist nie angekommen. Ob das am eigenen Netz liegt oder daran,
  // dass SaniUp nicht da ist, weiss nur der Browser.
  if (error.status === 0) {
    return online ? 'unreachable' : 'offline'
  }

  switch (error.status) {
    case 401:
      return 'unauthorized'
    case 403:
      return 'forbidden'
    case 404:
      return 'notFound'
    // 502/503/504 kommen im Betrieb praktisch immer vom Neustart eines App Service: der Dienst
    // braucht nach einem Deploy 2–3,5 Minuten, bis er antwortet (siehe SaniUp-Backend/CLAUDE.md).
    // Genau dafuer ist die Selbstheilung da.
    case 502:
    case 503:
    case 504:
      return 'unavailable'
    default:
      return error.status >= 500 ? 'server' : 'unknown'
  }
}

/**
 * Der Text zur Fehlerart, je Anrede: geduzt fuer Nutzer mit Login (Portal, interne App), gesiezt
 * fuer Patient:innen im Selfservice (`LIB_ANREDE`). Ein Satz zur Ursache und einer dazu, was jetzt
 * passiert, nie ein blosses „Ein Fehler ist aufgetreten".
 *
 * Die Sie-Fassung ist eine benannte Ausnahme im Anrede-Guard (`anrede-wording.util.spec.ts`).
 */
export function httpFailureCopy(kind: HttpFailureKind, anrede: Anrede = 'du'): HttpFailureCopy {
  const t = anrede === 'sie' ? SIE : DU
  switch (kind) {
    case 'offline':
      return { icon: 'wifi_off', title: 'Keine Internetverbindung', message: t.offline, retryLabel: 'Erneut versuchen' }
    case 'unreachable':
      return { icon: 'cloud_off', title: 'SaniUp ist gerade nicht erreichbar', message: t.unreachable, retryLabel: 'Erneut versuchen' }
    case 'unavailable':
      return {
        icon: 'cloud_sync',
        title: 'SaniUp startet gerade',
        message: 'Der Dienst ist kurz nicht bereit, das dauert meist ein bis drei Minuten. Wir versuchen es automatisch weiter.',
        retryLabel: 'Erneut versuchen',
      }
    case 'server':
      return { icon: 'error', title: 'Die Daten konnten nicht geladen werden', message: t.server, retryLabel: 'Erneut versuchen' }
    case 'unauthorized':
      return { icon: 'lock_clock', title: t.unauthorizedTitel, message: t.unauthorized, retryLabel: null }
    case 'forbidden':
      return { icon: 'lock', title: 'Keine Berechtigung', message: t.forbidden, retryLabel: null }
    case 'notFound':
      return {
        icon: 'search_off',
        title: 'Nicht gefunden',
        message: 'Diese Daten gibt es nicht mehr. Möglicherweise wurden sie zwischenzeitlich gelöscht.',
        retryLabel: null,
      }
    case 'unknown':
      return { icon: 'error', title: 'Die Daten konnten nicht geladen werden', message: t.unknown, retryLabel: 'Erneut versuchen' }
  }
}

interface AnredeTexte {
  offline: string
  unreachable: string
  server: string
  unauthorizedTitel: string
  unauthorized: string
  forbidden: string
  unknown: string
}

const DU: AnredeTexte = {
  offline: 'Dein Gerät ist gerade nicht mit dem Internet verbunden. Sobald die Verbindung zurück ist, laden wir automatisch nach.',
  unreachable: 'Die Daten konnten nicht geladen werden. Wir versuchen es automatisch weiter, du musst die Seite nicht neu laden.',
  server: 'Bei SaniUp ist etwas schiefgegangen. Versuch es erneut. Bleibt es dabei, hilft dir help@saniup.de weiter.',
  unauthorizedTitel: 'Deine Sitzung ist abgelaufen',
  unauthorized: 'Bitte melde dich erneut an, um weiterzuarbeiten.',
  forbidden: 'Für diese Daten ist dein Zugang nicht freigeschaltet. Dein Administrator kann das ändern.',
  unknown: 'Versuch es erneut. Bleibt es dabei, hilft dir help@saniup.de weiter.',
}

/** Fuer Patient:innen im Selfservice. */
const SIE: AnredeTexte = {
  offline: 'Ihr Gerät ist gerade nicht mit dem Internet verbunden. Sobald die Verbindung zurück ist, laden wir automatisch nach.',
  unreachable: 'Die Daten konnten nicht geladen werden. Wir versuchen es automatisch weiter, Sie müssen die Seite nicht neu laden.',
  server: 'Bei SaniUp ist etwas schiefgegangen. Versuchen Sie es erneut. Bleibt es dabei, hilft Ihnen help@saniup.de weiter.',
  unauthorizedTitel: 'Ihre Sitzung ist abgelaufen',
  unauthorized: 'Bitte melden Sie sich erneut an, um weiterzuarbeiten.',
  forbidden: 'Für diese Daten ist Ihr Zugang nicht freigeschaltet. Ihr Administrator kann das ändern.',
  unknown: 'Versuchen Sie es erneut. Bleibt es dabei, hilft Ihnen help@saniup.de weiter.',
}
